import * as Popover from "@radix-ui/react-popover";
import { Bell } from "lucide-react";
import { useState, type ComponentProps } from "react";

import { useUnreadCount } from "@/features/notifications/api";
import { NotificationPanel } from "@/features/notifications/components/NotificationPanel";
import { useNotificationActions } from "@/features/notifications/hooks/useNotificationActions";
import { badgeText } from "@/features/notifications/lib/badge";
import { useLocale, useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/Button";
import { Sheet } from "@/shared/ui/Sheet";

interface NotificationBellProps {
  /** "popover" next to the sidebar's workspace name; "sheet" in the phone's top bar. */
  presentation: "popover" | "sheet";
  className?: string;
}

function BellButton({
  count,
  className,
  ...props
}: { count: number } & ComponentProps<typeof Button>) {
  const t = useT();
  const { locale } = useLocale();
  const badge = badgeText(count, locale);
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={t("notifications.bellLabel", { count })}
      className={cn("relative", className)}
      {...props}
    >
      <Bell aria-hidden="true" />
      {badge ? (
        <span
          aria-hidden="true"
          className="absolute -end-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-danger-solid px-1 text-[11px] leading-none font-semibold text-on-solid tabular-nums ring-2 ring-canvas"
        >
          {badge}
        </span>
      ) : null}
    </Button>
  );
}

/**
 * The bell with the unread count, polled every 30 seconds while the tab is visible. It opens
 * the inbox in a popover on desktop and in a full-screen sheet on phones.
 */
export function NotificationBell({ presentation, className }: NotificationBellProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const count = useUnreadCount().data ?? 0;
  const actions = useNotificationActions(() => {
    setOpen(false);
  });
  const panel = (
    <NotificationPanel
      unreadCount={count}
      onOpen={actions.open}
      onReadAll={actions.readAll}
      markingAll={actions.markingAll}
      showTitle={presentation === "popover"}
    />
  );

  if (presentation === "sheet") {
    return (
      <>
        <BellButton
          count={count}
          className={className}
          aria-haspopup="dialog"
          onClick={() => {
            setOpen(true);
          }}
        />
        <Sheet
          open={open}
          onOpenChange={setOpen}
          label={t("notifications.title")}
          header={
            <h2 className="text-[15px] font-semibold text-ink">{t("notifications.title")}</h2>
          }
        >
          {panel}
        </Sheet>
      </>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <BellButton count={count} className={className} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          side="bottom"
          sideOffset={8}
          collisionPadding={12}
          aria-label={t("notifications.title")}
          className="z-70 flex max-h-[min(72vh,600px)] w-[min(420px,calc(100vw-1.5rem))] animate-pop-in flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-overlay outline-none"
        >
          {panel}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
